import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import entryGuideService from '../../services/entryGuideService';
import { getProjects } from '../../services/projectsService';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';

const ReceptionConsumeModal = ({
  isOpen,
  onClose,
  guideId,
  guideCode = '',
  onSuccess,
  onError,
}) => {
  const { receptionConsumeSingleProjectEnabled } = useModuleSettings();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [guide, setGuide] = useState(null);
  const [projects, setProjects] = useState([]);
  const [useSingleProject, setUseSingleProject] = useState(false);
  const [globalProjectId, setGlobalProjectId] = useState('');
  const [itemProjects, setItemProjects] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !guideId) return;

    let cancelled = false;
    setLoading(true);
    setError('');
    setUseSingleProject(receptionConsumeSingleProjectEnabled);
    setGlobalProjectId('');
    setItemProjects({});

    Promise.all([
      entryGuideService.getEntryGuide(guideId),
      getProjects({ active_only: true }),
    ])
      .then(([guideRes, projectsRes]) => {
        if (cancelled) return;
        if (!guideRes.success || !guideRes.data) {
          setError('No se pudo cargar la guía de recepción');
          setGuide(null);
          return;
        }
        setGuide(guideRes.data);
        const initialProjects = {};
        (guideRes.data.items || []).forEach((item) => {
          initialProjects[item.id] = item.project_id ? String(item.project_id) : '';
        });
        setItemProjects(initialProjects);
        setProjects(projectsRes.success && Array.isArray(projectsRes.data) ? projectsRes.data : []);
      })
      .catch(() => {
        if (!cancelled) {
          setError('No se pudo cargar la información para consumir');
          setGuide(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, guideId, receptionConsumeSingleProjectEnabled]);

  const items = guide?.items || [];

  const totalAmount = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.total || 0), 0),
    [items],
  );

  const applyGlobalProjectToItems = (projectId) => {
    setItemProjects((prev) => {
      const next = { ...prev };
      items.forEach((item) => {
        next[item.id] = projectId;
      });
      return next;
    });
  };

  const handleToggleSingleProject = (checked) => {
    setUseSingleProject(checked);
    setError('');
    if (checked && globalProjectId) {
      applyGlobalProjectToItems(globalProjectId);
    }
  };

  const handleGlobalProjectChange = (projectId) => {
    setGlobalProjectId(projectId);
    if (useSingleProject) {
      applyGlobalProjectToItems(projectId);
    }
  };

  const handleItemProjectChange = (itemId, projectId) => {
    setItemProjects((prev) => ({
      ...prev,
      [itemId]: projectId,
    }));
  };

  const validateProjects = () => {
    if (useSingleProject) {
      if (!globalProjectId) {
        setError('Seleccione el proyecto para consumir todos los productos');
        return false;
      }
      return true;
    }

    const missing = items.some((item) => !itemProjects[item.id]);
    if (missing) {
      setError('Todos los productos deben tener un proyecto asignado');
      return false;
    }
    return true;
  };

  const handleConfirm = async () => {
    setError('');
    if (!validateProjects()) return;

    setSaving(true);
    try {
      const payload = useSingleProject
        ? { global_project_id: parseInt(globalProjectId, 10) }
        : {
            items: items.map((item) => ({
              item_id: item.id,
              project_id: parseInt(itemProjects[item.id], 10),
            })),
          };

      const response = await entryGuideService.consumeReception(guideId, payload);
      if (!response.success) {
        throw new Error(response.message || 'No se pudo registrar el consumo');
      }
      if (onSuccess) onSuccess(response.data);
      onClose();
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Error al consumir la recepción';
      setError(message);
      if (onError) onError(message);
    } finally {
      setSaving(false);
    }
  };

  const projectLabel = (projectId) => {
    if (!projectId) return '—';
    const project = projects.find((p) => String(p.id) === String(projectId));
    return project ? `${project.code} - ${project.name}` : '—';
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10050] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 shrink-0">Consumir recepción</h3>
            {guideCode ? (
              <span className="text-xs font-mono text-teal-700 bg-teal-50 border border-teal-100 px-2 py-0.5 rounded truncate">
                {guideCode}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50 shrink-0"
            aria-label="Cerrar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <p className="text-sm text-gray-500 py-6 text-center">Cargando productos...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">La recepción no tiene productos para consumir.</p>
          ) : (
            <div className="space-y-3">
              <div className="rounded-md border border-gray-200 bg-gray-50/80 px-3 py-2.5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <label className="flex items-center gap-2 cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={useSingleProject}
                      onChange={(e) => handleToggleSingleProject(e.target.checked)}
                      disabled={saving}
                      className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-800">Un solo proyecto para todos</span>
                  </label>

                  {useSingleProject && (
                    <select
                      value={globalProjectId}
                      onChange={(e) => handleGlobalProjectChange(e.target.value)}
                      disabled={saving}
                      className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
                    >
                      <option value="">Seleccionar proyecto</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.code} - {project.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto rounded-md border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Producto</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide w-20">Cant.</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide w-14">Und.</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide w-24">C. unit.</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide w-24">Total</th>
                      {!useSingleProject && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide min-w-[11rem]">Proyecto</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {items.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50/50">
                        <td className="px-3 py-2 text-gray-900">
                          <div className="font-medium leading-tight">{item.product?.name || item.description || 'Producto'}</div>
                          {item.product?.sku ? (
                            <div className="text-xs text-gray-400 mt-0.5 truncate max-w-[12rem]">{item.product.sku}</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-gray-900 text-right tabular-nums">
                          {Number(item.quantity || 0).toFixed(3)}
                        </td>
                        <td className="px-3 py-2 text-gray-600">{item.unit_of_measure || '—'}</td>
                        <td className="px-3 py-2 text-gray-900 text-right tabular-nums whitespace-nowrap">
                          S/ {Number(item.unit_price || 0).toFixed(4)}
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-900 text-right tabular-nums whitespace-nowrap">
                          S/ {Number(item.total || 0).toFixed(4)}
                        </td>
                        {!useSingleProject && (
                          <td className="px-3 py-2">
                            <select
                              value={itemProjects[item.id] || ''}
                              onChange={(e) => handleItemProjectChange(item.id, e.target.value)}
                              disabled={saving}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                            >
                              <option value="">Seleccionar</option>
                              {projects.map((project) => (
                                <option key={project.id} value={project.id}>
                                  {project.code} - {project.name}
                                </option>
                              ))}
                            </select>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-t border-gray-200 text-sm">
                  <span className="text-gray-600">
                    {items.length} {items.length === 1 ? 'producto' : 'productos'}
                    {useSingleProject && globalProjectId ? (
                      <span className="text-gray-400"> · {projectLabel(globalProjectId)}</span>
                    ) : null}
                  </span>
                  <span className="font-semibold text-gray-900 tabular-nums">
                    Total S/ {totalAmount.toFixed(4)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={saving || loading || items.length === 0}
            className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md disabled:opacity-50"
          >
            {saving ? 'Consumiendo...' : 'Confirmar consumo'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ReceptionConsumeModal;
